import React from "react";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  usePagination,
} from "react-table";
import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/solid";
import { GoSearch } from "react-icons/go";
import { Button, PageButton } from "./shared/Button";
import { classNames } from "./shared/Utils";
import { SortIcon, SortUpIcon, SortDownIcon } from "./shared/Icons";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <div className="text-gray-600 focus-within:text-gray-400 flex w-full justify-end py-4 px-4 items-center ">
      <div className="float-right ml-auto mr-20 -mt-8">
        <div className="svgdiv">
          <svg
            width="22"
            height="19"
            viewBox="0 0 22 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.8122 6.30951H0.90932C0.635938 6.30951 0.414062 6.1333 0.414062 5.91619V3.84382C0.414062 2.78836 1.49521 1.92975 2.82423 1.92975H18.8973C20.2261 1.92975 21.3072 2.78836 21.3072 3.84382V5.91639C21.3075 6.1335 21.0856 6.30951 20.8122 6.30951ZM1.40458 5.52288H20.317V3.84362C20.317 3.22199 19.6803 2.71618 18.8976 2.71618H2.82423C2.04123 2.71618 1.40458 3.22199 1.40458 3.84362V5.52288Z"
              fill="#1F78B4"
            />
            <path
              d="M10.3977 18.3441H2.82423C1.49521 18.3441 0.414062 17.4854 0.414062 16.43V5.91383C0.414062 5.69671 0.635938 5.52051 0.90932 5.52051H20.8122C21.0856 5.52051 21.3075 5.69671 21.3075 5.91383V9.67985C21.3075 9.89696 21.0856 10.0732 20.8122 10.0732C20.5389 10.0732 20.317 9.89696 20.317 9.67985V6.30714H1.40458V16.4298C1.40458 17.0514 2.04148 17.5572 2.82423 17.5572H10.3977C10.6711 17.5572 10.893 17.7334 10.893 17.9505C10.893 18.1677 10.6713 18.3441 10.3977 18.3441Z"
              fill="#1F78B4"
            />
            <path
              d="M4.87805 3.99841C4.60468 3.99841 4.38281 3.82221 4.38281 3.6051V1.03968C4.38281 0.822569 4.60468 0.646362 4.87805 0.646362C5.15142 0.646362 5.37329 0.822569 5.37329 1.03968V3.6051C5.37329 3.82221 5.15167 3.99841 4.87805 3.99841Z"
              fill="#1F78B4"
            />
            <path
              d="M16.839 3.99841C16.5656 3.99841 16.3438 3.82221 16.3438 3.6051V1.03968C16.3438 0.822569 16.5656 0.646362 16.839 0.646362C17.1124 0.646362 17.3342 0.822569 17.3342 1.03968V3.6051C17.3342 3.82221 17.1124 3.99841 16.839 3.99841Z"
              fill="#1F78B4"
            />
            <path
              d="M10.8624 3.99841C10.5891 3.99841 10.3672 3.82221 10.3672 3.6051V1.03968C10.3672 0.822569 10.5891 0.646362 10.8624 0.646362C11.1358 0.646362 11.3577 0.822569 11.3577 1.03968V3.6051C11.3577 3.82221 11.1358 3.99841 10.8624 3.99841Z"
              fill="#1F78B4"
            />
            <path
              d="M4.87093 8.90326H4.49479C4.32518 8.90326 4.1875 8.79411 4.1875 8.6592V8.36046C4.1875 8.22574 4.32493 8.11639 4.49479 8.11639H4.87093C5.04055 8.11639 5.17822 8.22554 5.17822 8.36046V8.6592C5.17822 8.79411 5.04079 8.90326 4.87093 8.90326Z"
              fill="#1F78B4"
            />
            <path
              d="M8.98812 8.90326H8.61198C8.44236 8.90326 8.30469 8.79411 8.30469 8.6592V8.36046C8.30469 8.22574 8.44212 8.11639 8.61198 8.11639H8.98812C9.15773 8.11639 9.29541 8.22554 9.29541 8.36046V8.6592C9.29541 8.79411 9.15798 8.90326 8.98812 8.90326Z"
              fill="#1F78B4"
            />
            <path
              d="M13.1053 8.90326H12.7292C12.5596 8.90326 12.4219 8.79411 12.4219 8.6592V8.36046C12.4219 8.22574 12.5593 8.11639 12.7292 8.11639H13.1053C13.2749 8.11639 13.4126 8.22554 13.4126 8.36046V8.6592C13.4126 8.79411 13.2752 8.90326 13.1053 8.90326Z"
              fill="#1F78B4"
            />
            <path
              d="M17.2225 8.90326H16.8464C16.6767 8.90326 16.5391 8.79411 16.5391 8.6592V8.36046C16.5391 8.22574 16.6765 8.11639 16.8464 8.11639H17.2225C17.3921 8.11639 17.5298 8.22554 17.5298 8.36046V8.6592C17.5295 8.79411 17.3921 8.90326 17.2225 8.90326Z"
              fill="#1F78B4"
            />
            <path
              d="M4.87093 12.1732H4.49479C4.32518 12.1732 4.1875 12.064 4.1875 11.9291V11.6304C4.1875 11.4957 4.32493 11.3864 4.49479 11.3864H4.87093C5.04055 11.3864 5.17822 11.4955 5.17822 11.6304V11.9291C5.17822 12.064 5.04079 12.1732 4.87093 12.1732Z"
              fill="#1F78B4"
            />
            <path
              d="M8.98812 12.1732H8.61198C8.44236 12.1732 8.30469 12.064 8.30469 11.9291V11.6304C8.30469 11.4957 8.44212 11.3864 8.61198 11.3864H8.98812C9.15773 11.3864 9.29541 11.4955 9.29541 11.6304V11.9291C9.29541 12.064 9.15798 12.1732 8.98812 12.1732Z"
              fill="#1F78B4"
            />
            <path
              d="M4.87093 15.4464H4.49479C4.32518 15.4464 4.1875 15.3373 4.1875 15.2023V14.9036C4.1875 14.7689 4.32493 14.6595 4.49479 14.6595H4.87093C5.04055 14.6595 5.17822 14.7687 5.17822 14.9036V15.2023C5.17822 15.3373 5.04079 15.4464 4.87093 15.4464Z"
              fill="#1F78B4"
            />
            <path
              d="M8.98812 15.4464H8.61198C8.44236 15.4464 8.30469 15.3373 8.30469 15.2023V14.9036C8.30469 14.7689 8.44212 14.6595 8.61198 14.6595H8.98812C9.15773 14.6595 9.29541 14.7687 9.29541 14.9036V15.2023C9.29541 15.3373 9.15798 15.4464 8.98812 15.4464Z"
              fill="#1F78B4"
            />
            <path
              d="M16.2512 18.3475C13.4633 18.3475 11.1953 16.5463 11.1953 14.3323C11.1953 12.1181 13.4633 10.3169 16.2512 10.3169C19.039 10.3169 21.307 12.1181 21.307 14.3323C21.307 16.5463 19.0387 18.3475 16.2512 18.3475ZM16.2512 11.1035C14.0094 11.1035 12.1858 12.5519 12.1858 14.3323C12.1858 16.1126 14.0094 17.5608 16.2512 17.5608C18.4929 17.5608 20.3165 16.1126 20.3165 14.3323C20.3165 12.5519 18.4927 11.1035 16.2512 11.1035Z"
              fill="#1F78B4"
            />
            <path
              d="M15.7338 15.7985C15.7298 15.7985 15.7258 15.7985 15.7219 15.7983C15.5825 15.7957 15.4507 15.7463 15.3594 15.6628L14.3084 14.6999C14.1294 14.5359 14.1517 14.2874 14.3585 14.1452C14.565 14.0032 14.8782 14.0203 15.057 14.1849L15.7514 14.8211L17.72 13.1837C17.9087 13.0262 18.2224 13.0209 18.42 13.1709C18.6179 13.321 18.6251 13.5698 18.4361 13.7269L16.0916 15.6769C15.9982 15.7546 15.869 15.7985 15.7338 15.7985Z"
              fill="#1F78B4"
            />
          </svg>
        </div>
        <div className="svgdiv float-right relative -top-4 right-12">
          <svg
            width="22"
            height="19"
            viewBox="0 0 22 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.8122 6.30951H0.90932C0.635938 6.30951 0.414062 6.1333 0.414062 5.91619V3.84382C0.414062 2.78836 1.49521 1.92975 2.82423 1.92975H18.8973C20.2261 1.92975 21.3072 2.78836 21.3072 3.84382V5.91639C21.3075 6.1335 21.0856 6.30951 20.8122 6.30951ZM1.40458 5.52288H20.317V3.84362C20.317 3.22199 19.6803 2.71618 18.8976 2.71618H2.82423C2.04123 2.71618 1.40458 3.22199 1.40458 3.84362V5.52288Z"
              fill="#1F78B4"
            />
            <path
              d="M10.3977 18.3441H2.82423C1.49521 18.3441 0.414062 17.4854 0.414062 16.43V5.91383C0.414062 5.69671 0.635938 5.52051 0.90932 5.52051H20.8122C21.0856 5.52051 21.3075 5.69671 21.3075 5.91383V9.67985C21.3075 9.89696 21.0856 10.0732 20.8122 10.0732C20.5389 10.0732 20.317 9.89696 20.317 9.67985V6.30714H1.40458V16.4298C1.40458 17.0514 2.04148 17.5572 2.82423 17.5572H10.3977C10.6711 17.5572 10.893 17.7334 10.893 17.9505C10.893 18.1677 10.6713 18.3441 10.3977 18.3441Z"
              fill="#1F78B4"
            />
            <path
              d="M4.87805 3.99841C4.60468 3.99841 4.38281 3.82221 4.38281 3.6051V1.03968C4.38281 0.822569 4.60468 0.646362 4.87805 0.646362C5.15142 0.646362 5.37329 0.822569 5.37329 1.03968V3.6051C5.37329 3.82221 5.15167 3.99841 4.87805 3.99841Z"
              fill="#1F78B4"
            />
            <path
              d="M16.839 3.99841C16.5656 3.99841 16.3438 3.82221 16.3438 3.6051V1.03968C16.3438 0.822569 16.5656 0.646362 16.839 0.646362C17.1124 0.646362 17.3342 0.822569 17.3342 1.03968V3.6051C17.3342 3.82221 17.1124 3.99841 16.839 3.99841Z"
              fill="#1F78B4"
            />
            <path
              d="M10.8624 3.99841C10.5891 3.99841 10.3672 3.82221 10.3672 3.6051V1.03968C10.3672 0.822569 10.5891 0.646362 10.8624 0.646362C11.1358 0.646362 11.3577 0.822569 11.3577 1.03968V3.6051C11.3577 3.82221 11.1358 3.99841 10.8624 3.99841Z"
              fill="#1F78B4"
            />
            <path
              d="M4.87093 8.90326H4.49479C4.32518 8.90326 4.1875 8.79411 4.1875 8.6592V8.36046C4.1875 8.22574 4.32493 8.11639 4.49479 8.11639H4.87093C5.04055 8.11639 5.17822 8.22554 5.17822 8.36046V8.6592C5.17822 8.79411 5.04079 8.90326 4.87093 8.90326Z"
              fill="#1F78B4"
            />
            <path
              d="M8.98812 8.90326H8.61198C8.44236 8.90326 8.30469 8.79411 8.30469 8.6592V8.36046C8.30469 8.22574 8.44212 8.11639 8.61198 8.11639H8.98812C9.15773 8.11639 9.29541 8.22554 9.29541 8.36046V8.6592C9.29541 8.79411 9.15798 8.90326 8.98812 8.90326Z"
              fill="#1F78B4"
            />
            <path
              d="M13.1053 8.90326H12.7292C12.5596 8.90326 12.4219 8.79411 12.4219 8.6592V8.36046C12.4219 8.22574 12.5593 8.11639 12.7292 8.11639H13.1053C13.2749 8.11639 13.4126 8.22554 13.4126 8.36046V8.6592C13.4126 8.79411 13.2752 8.90326 13.1053 8.90326Z"
              fill="#1F78B4"
            />
            <path
              d="M17.2225 8.90326H16.8464C16.6767 8.90326 16.5391 8.79411 16.5391 8.6592V8.36046C16.5391 8.22574 16.6765 8.11639 16.8464 8.11639H17.2225C17.3921 8.11639 17.5298 8.22554 17.5298 8.36046V8.6592C17.5295 8.79411 17.3921 8.90326 17.2225 8.90326Z"
              fill="#1F78B4"
            />
            <path
              d="M4.87093 12.1732H4.49479C4.32518 12.1732 4.1875 12.064 4.1875 11.9291V11.6304C4.1875 11.4957 4.32493 11.3864 4.49479 11.3864H4.87093C5.04055 11.3864 5.17822 11.4955 5.17822 11.6304V11.9291C5.17822 12.064 5.04079 12.1732 4.87093 12.1732Z"
              fill="#1F78B4"
            />
            <path
              d="M8.98812 12.1732H8.61198C8.44236 12.1732 8.30469 12.064 8.30469 11.9291V11.6304C8.30469 11.4957 8.44212 11.3864 8.61198 11.3864H8.98812C9.15773 11.3864 9.29541 11.4955 9.29541 11.6304V11.9291C9.29541 12.064 9.15798 12.1732 8.98812 12.1732Z"
              fill="#1F78B4"
            />
            <path
              d="M4.87093 15.4464H4.49479C4.32518 15.4464 4.1875 15.3373 4.1875 15.2023V14.9036C4.1875 14.7689 4.32493 14.6595 4.49479 14.6595H4.87093C5.04055 14.6595 5.17822 14.7687 5.17822 14.9036V15.2023C5.17822 15.3373 5.04079 15.4464 4.87093 15.4464Z"
              fill="#1F78B4"
            />
            <path
              d="M8.98812 15.4464H8.61198C8.44236 15.4464 8.30469 15.3373 8.30469 15.2023V14.9036C8.30469 14.7689 8.44212 14.6595 8.61198 14.6595H8.98812C9.15773 14.6595 9.29541 14.7687 9.29541 14.9036V15.2023C9.29541 15.3373 9.15798 15.4464 8.98812 15.4464Z"
              fill="#1F78B4"
            />
            <path
              d="M16.2512 18.3475C13.4633 18.3475 11.1953 16.5463 11.1953 14.3323C11.1953 12.1181 13.4633 10.3169 16.2512 10.3169C19.039 10.3169 21.307 12.1181 21.307 14.3323C21.307 16.5463 19.0387 18.3475 16.2512 18.3475ZM16.2512 11.1035C14.0094 11.1035 12.1858 12.5519 12.1858 14.3323C12.1858 16.1126 14.0094 17.5608 16.2512 17.5608C18.4929 17.5608 20.3165 16.1126 20.3165 14.3323C20.3165 12.5519 18.4927 11.1035 16.2512 11.1035Z"
              fill="#1F78B4"
            />
            <path
              d="M15.7338 15.7985C15.7298 15.7985 15.7258 15.7985 15.7219 15.7983C15.5825 15.7957 15.4507 15.7463 15.3594 15.6628L14.3084 14.6999C14.1294 14.5359 14.1517 14.2874 14.3585 14.1452C14.565 14.0032 14.8782 14.0203 15.057 14.1849L15.7514 14.8211L17.72 13.1837C17.9087 13.0262 18.2224 13.0209 18.42 13.1709C18.6179 13.321 18.6251 13.5698 18.4361 13.7269L16.0916 15.6769C15.9982 15.7546 15.869 15.7985 15.7338 15.7985Z"
              fill="#1F78B4"
            />
          </svg>
        </div>
        <div class=" flex border-b border-blue-200 mr-6 ">
          <input
            class="w-full bg-transparent rounded-lg p-2 outline-none placeholder-blue-200"
            type="text"
            value={value || ""}
            onChange={(e) => {
              setValue(e.target.value);
              onChange(e.target.value);
            }}
            placeholder="Search"
          />
          <button class=" w-auto flex justify-end items-center text-blue p-2 hover:text-blue-light">
            <svg
              width="19"
              height="19"
              viewBox="0 0 19 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.1206 13.7553L13.5772 9.87719C14.4317 8.87406 14.9369 7.64031 14.9369 6.30781C14.9369 2.95469 11.7409 0.226562 7.81186 0.226562C3.88358 0.226562 0.6875 2.95469 0.6875 6.30781C0.6875 9.66156 3.88358 12.3891 7.81186 12.3891C9.30116 12.3891 10.6843 11.9972 11.8295 11.3278L16.397 15.2266L18.1206 13.7553ZM2.77721 6.30781C2.77721 3.93781 5.03607 2.00969 7.81259 2.00969C10.5891 2.00969 12.848 3.93781 12.848 6.30781C12.848 8.67781 10.5891 10.6059 7.81259 10.6059C5.03534 10.6059 2.77721 8.67781 2.77721 6.30781Z"
                fill="#1F78B4"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// This is a custom filter UI for selecting
// a unique option from a list

export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id, render },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  // return (
  //   <label className="flex gap-x-2 items-baseline">
  //     <span className="text-gray-100 mx-3">{render("Header")}:</span>
  //     <select
  //       className="form-select appearance-none
  //       block
  //       px-4
  //       py-1
  //       text-sm
  //       font-Montserrat
  //       text-gray-700
  //       bg-white bg-clip-padding bg-no-repeat
  //       border border-solid border-gray-100
  //       rounded-lg
  //       transition
  //       ease-in-out
  //       m-0
  //       focus:text-gray-700 focus:border-blue-100 focus:outline-none"
  //       name={id}
  //       id={id}
  //       value={filterValue}
  //       onChange={(e) => {
  //         setFilter(e.target.value || undefined);
  //       }}
  //     >
  //       <option selected>All</option>
  //       {options.map((option, i) => (
  //         <option key={i} value={option}>
  //           {option}
  //         </option>
  //       ))}
  //     </select>
  //   </label>
  // );
}

export function StatusPill({ value }) {
  const status = value ? value.toLowerCase() : "unknown";

  return (
    <span
      className={classNames(
        "px-3 py-1 uppercase leading-wide text-xs rounded-full shadow-sm",
        status.startsWith("active")
          ? "bg-green-100 text-white"
          : status.startsWith("discontinued")
          ? "bg-red-800 text-white"
          : null
      )}
    >
      {status}
    </span>
  );
}

export function AvatarCell({ value, column, row }) {
  return (
    <div className="flex items-center">
      <div className="flex-shrink-0 h-10 w-10">
        <img
          className="h-10 w-10 rounded-full"
          src="https://image.shutterstock.com/image-photo/closeup-elegant-mens-shoes-on-260nw-156770609.jpg"
          alt=""
        />
      </div>
      <div className="ml-4">
        <div className="text-sm font-medium text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">
          {row.original[column.emailAccessor]}
        </div>
      </div>
    </div>
  );
}
export function LeadtimeCell({ value, column, row }) {
  return (
    <div className="shadow-lg shadow-slate-300 bg-transparent w-max p-2 rounded-lg">
      <div className="svgdiv">
        <svg
          width="22"
          height="19"
          viewBox="0 0 22 19"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.8122 6.30951H0.90932C0.635938 6.30951 0.414062 6.1333 0.414062 5.91619V3.84382C0.414062 2.78836 1.49521 1.92975 2.82423 1.92975H18.8973C20.2261 1.92975 21.3072 2.78836 21.3072 3.84382V5.91639C21.3075 6.1335 21.0856 6.30951 20.8122 6.30951ZM1.40458 5.52288H20.317V3.84362C20.317 3.22199 19.6803 2.71618 18.8976 2.71618H2.82423C2.04123 2.71618 1.40458 3.22199 1.40458 3.84362V5.52288Z"
            fill="#1F78B4"
          />
          <path
            d="M10.3977 18.3441H2.82423C1.49521 18.3441 0.414062 17.4854 0.414062 16.43V5.91383C0.414062 5.69671 0.635938 5.52051 0.90932 5.52051H20.8122C21.0856 5.52051 21.3075 5.69671 21.3075 5.91383V9.67985C21.3075 9.89696 21.0856 10.0732 20.8122 10.0732C20.5389 10.0732 20.317 9.89696 20.317 9.67985V6.30714H1.40458V16.4298C1.40458 17.0514 2.04148 17.5572 2.82423 17.5572H10.3977C10.6711 17.5572 10.893 17.7334 10.893 17.9505C10.893 18.1677 10.6713 18.3441 10.3977 18.3441Z"
            fill="#1F78B4"
          />
          <path
            d="M4.87805 3.99841C4.60468 3.99841 4.38281 3.82221 4.38281 3.6051V1.03968C4.38281 0.822569 4.60468 0.646362 4.87805 0.646362C5.15142 0.646362 5.37329 0.822569 5.37329 1.03968V3.6051C5.37329 3.82221 5.15167 3.99841 4.87805 3.99841Z"
            fill="#1F78B4"
          />
          <path
            d="M16.839 3.99841C16.5656 3.99841 16.3438 3.82221 16.3438 3.6051V1.03968C16.3438 0.822569 16.5656 0.646362 16.839 0.646362C17.1124 0.646362 17.3342 0.822569 17.3342 1.03968V3.6051C17.3342 3.82221 17.1124 3.99841 16.839 3.99841Z"
            fill="#1F78B4"
          />
          <path
            d="M10.8624 3.99841C10.5891 3.99841 10.3672 3.82221 10.3672 3.6051V1.03968C10.3672 0.822569 10.5891 0.646362 10.8624 0.646362C11.1358 0.646362 11.3577 0.822569 11.3577 1.03968V3.6051C11.3577 3.82221 11.1358 3.99841 10.8624 3.99841Z"
            fill="#1F78B4"
          />
          <path
            d="M4.87093 8.90326H4.49479C4.32518 8.90326 4.1875 8.79411 4.1875 8.6592V8.36046C4.1875 8.22574 4.32493 8.11639 4.49479 8.11639H4.87093C5.04055 8.11639 5.17822 8.22554 5.17822 8.36046V8.6592C5.17822 8.79411 5.04079 8.90326 4.87093 8.90326Z"
            fill="#1F78B4"
          />
          <path
            d="M8.98812 8.90326H8.61198C8.44236 8.90326 8.30469 8.79411 8.30469 8.6592V8.36046C8.30469 8.22574 8.44212 8.11639 8.61198 8.11639H8.98812C9.15773 8.11639 9.29541 8.22554 9.29541 8.36046V8.6592C9.29541 8.79411 9.15798 8.90326 8.98812 8.90326Z"
            fill="#1F78B4"
          />
          <path
            d="M13.1053 8.90326H12.7292C12.5596 8.90326 12.4219 8.79411 12.4219 8.6592V8.36046C12.4219 8.22574 12.5593 8.11639 12.7292 8.11639H13.1053C13.2749 8.11639 13.4126 8.22554 13.4126 8.36046V8.6592C13.4126 8.79411 13.2752 8.90326 13.1053 8.90326Z"
            fill="#1F78B4"
          />
          <path
            d="M17.2225 8.90326H16.8464C16.6767 8.90326 16.5391 8.79411 16.5391 8.6592V8.36046C16.5391 8.22574 16.6765 8.11639 16.8464 8.11639H17.2225C17.3921 8.11639 17.5298 8.22554 17.5298 8.36046V8.6592C17.5295 8.79411 17.3921 8.90326 17.2225 8.90326Z"
            fill="#1F78B4"
          />
          <path
            d="M4.87093 12.1732H4.49479C4.32518 12.1732 4.1875 12.064 4.1875 11.9291V11.6304C4.1875 11.4957 4.32493 11.3864 4.49479 11.3864H4.87093C5.04055 11.3864 5.17822 11.4955 5.17822 11.6304V11.9291C5.17822 12.064 5.04079 12.1732 4.87093 12.1732Z"
            fill="#1F78B4"
          />
          <path
            d="M8.98812 12.1732H8.61198C8.44236 12.1732 8.30469 12.064 8.30469 11.9291V11.6304C8.30469 11.4957 8.44212 11.3864 8.61198 11.3864H8.98812C9.15773 11.3864 9.29541 11.4955 9.29541 11.6304V11.9291C9.29541 12.064 9.15798 12.1732 8.98812 12.1732Z"
            fill="#1F78B4"
          />
          <path
            d="M4.87093 15.4464H4.49479C4.32518 15.4464 4.1875 15.3373 4.1875 15.2023V14.9036C4.1875 14.7689 4.32493 14.6595 4.49479 14.6595H4.87093C5.04055 14.6595 5.17822 14.7687 5.17822 14.9036V15.2023C5.17822 15.3373 5.04079 15.4464 4.87093 15.4464Z"
            fill="#1F78B4"
          />
          <path
            d="M8.98812 15.4464H8.61198C8.44236 15.4464 8.30469 15.3373 8.30469 15.2023V14.9036C8.30469 14.7689 8.44212 14.6595 8.61198 14.6595H8.98812C9.15773 14.6595 9.29541 14.7687 9.29541 14.9036V15.2023C9.29541 15.3373 9.15798 15.4464 8.98812 15.4464Z"
            fill="#1F78B4"
          />
          <path
            d="M16.2512 18.3475C13.4633 18.3475 11.1953 16.5463 11.1953 14.3323C11.1953 12.1181 13.4633 10.3169 16.2512 10.3169C19.039 10.3169 21.307 12.1181 21.307 14.3323C21.307 16.5463 19.0387 18.3475 16.2512 18.3475ZM16.2512 11.1035C14.0094 11.1035 12.1858 12.5519 12.1858 14.3323C12.1858 16.1126 14.0094 17.5608 16.2512 17.5608C18.4929 17.5608 20.3165 16.1126 20.3165 14.3323C20.3165 12.5519 18.4927 11.1035 16.2512 11.1035Z"
            fill="#1F78B4"
          />
          <path
            d="M15.7338 15.7985C15.7298 15.7985 15.7258 15.7985 15.7219 15.7983C15.5825 15.7957 15.4507 15.7463 15.3594 15.6628L14.3084 14.6999C14.1294 14.5359 14.1517 14.2874 14.3585 14.1452C14.565 14.0032 14.8782 14.0203 15.057 14.1849L15.7514 14.8211L17.72 13.1837C17.9087 13.0262 18.2224 13.0209 18.42 13.1709C18.6179 13.321 18.6251 13.5698 18.4361 13.7269L16.0916 15.6769C15.9982 15.7546 15.869 15.7985 15.7338 15.7985Z"
            fill="#1F78B4"
          />
        </svg>
      </div>
      <div className="svgdiv float-right relative -top-5 right-8">
        <svg
          width="22"
          height="19"
          viewBox="0 0 22 19"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.8122 6.30951H0.90932C0.635938 6.30951 0.414062 6.1333 0.414062 5.91619V3.84382C0.414062 2.78836 1.49521 1.92975 2.82423 1.92975H18.8973C20.2261 1.92975 21.3072 2.78836 21.3072 3.84382V5.91639C21.3075 6.1335 21.0856 6.30951 20.8122 6.30951ZM1.40458 5.52288H20.317V3.84362C20.317 3.22199 19.6803 2.71618 18.8976 2.71618H2.82423C2.04123 2.71618 1.40458 3.22199 1.40458 3.84362V5.52288Z"
            fill="#1F78B4"
          />
          <path
            d="M10.3977 18.3441H2.82423C1.49521 18.3441 0.414062 17.4854 0.414062 16.43V5.91383C0.414062 5.69671 0.635938 5.52051 0.90932 5.52051H20.8122C21.0856 5.52051 21.3075 5.69671 21.3075 5.91383V9.67985C21.3075 9.89696 21.0856 10.0732 20.8122 10.0732C20.5389 10.0732 20.317 9.89696 20.317 9.67985V6.30714H1.40458V16.4298C1.40458 17.0514 2.04148 17.5572 2.82423 17.5572H10.3977C10.6711 17.5572 10.893 17.7334 10.893 17.9505C10.893 18.1677 10.6713 18.3441 10.3977 18.3441Z"
            fill="#1F78B4"
          />
          <path
            d="M4.87805 3.99841C4.60468 3.99841 4.38281 3.82221 4.38281 3.6051V1.03968C4.38281 0.822569 4.60468 0.646362 4.87805 0.646362C5.15142 0.646362 5.37329 0.822569 5.37329 1.03968V3.6051C5.37329 3.82221 5.15167 3.99841 4.87805 3.99841Z"
            fill="#1F78B4"
          />
          <path
            d="M16.839 3.99841C16.5656 3.99841 16.3438 3.82221 16.3438 3.6051V1.03968C16.3438 0.822569 16.5656 0.646362 16.839 0.646362C17.1124 0.646362 17.3342 0.822569 17.3342 1.03968V3.6051C17.3342 3.82221 17.1124 3.99841 16.839 3.99841Z"
            fill="#1F78B4"
          />
          <path
            d="M10.8624 3.99841C10.5891 3.99841 10.3672 3.82221 10.3672 3.6051V1.03968C10.3672 0.822569 10.5891 0.646362 10.8624 0.646362C11.1358 0.646362 11.3577 0.822569 11.3577 1.03968V3.6051C11.3577 3.82221 11.1358 3.99841 10.8624 3.99841Z"
            fill="#1F78B4"
          />
          <path
            d="M4.87093 8.90326H4.49479C4.32518 8.90326 4.1875 8.79411 4.1875 8.6592V8.36046C4.1875 8.22574 4.32493 8.11639 4.49479 8.11639H4.87093C5.04055 8.11639 5.17822 8.22554 5.17822 8.36046V8.6592C5.17822 8.79411 5.04079 8.90326 4.87093 8.90326Z"
            fill="#1F78B4"
          />
          <path
            d="M8.98812 8.90326H8.61198C8.44236 8.90326 8.30469 8.79411 8.30469 8.6592V8.36046C8.30469 8.22574 8.44212 8.11639 8.61198 8.11639H8.98812C9.15773 8.11639 9.29541 8.22554 9.29541 8.36046V8.6592C9.29541 8.79411 9.15798 8.90326 8.98812 8.90326Z"
            fill="#1F78B4"
          />
          <path
            d="M13.1053 8.90326H12.7292C12.5596 8.90326 12.4219 8.79411 12.4219 8.6592V8.36046C12.4219 8.22574 12.5593 8.11639 12.7292 8.11639H13.1053C13.2749 8.11639 13.4126 8.22554 13.4126 8.36046V8.6592C13.4126 8.79411 13.2752 8.90326 13.1053 8.90326Z"
            fill="#1F78B4"
          />
          <path
            d="M17.2225 8.90326H16.8464C16.6767 8.90326 16.5391 8.79411 16.5391 8.6592V8.36046C16.5391 8.22574 16.6765 8.11639 16.8464 8.11639H17.2225C17.3921 8.11639 17.5298 8.22554 17.5298 8.36046V8.6592C17.5295 8.79411 17.3921 8.90326 17.2225 8.90326Z"
            fill="#1F78B4"
          />
          <path
            d="M4.87093 12.1732H4.49479C4.32518 12.1732 4.1875 12.064 4.1875 11.9291V11.6304C4.1875 11.4957 4.32493 11.3864 4.49479 11.3864H4.87093C5.04055 11.3864 5.17822 11.4955 5.17822 11.6304V11.9291C5.17822 12.064 5.04079 12.1732 4.87093 12.1732Z"
            fill="#1F78B4"
          />
          <path
            d="M8.98812 12.1732H8.61198C8.44236 12.1732 8.30469 12.064 8.30469 11.9291V11.6304C8.30469 11.4957 8.44212 11.3864 8.61198 11.3864H8.98812C9.15773 11.3864 9.29541 11.4955 9.29541 11.6304V11.9291C9.29541 12.064 9.15798 12.1732 8.98812 12.1732Z"
            fill="#1F78B4"
          />
          <path
            d="M4.87093 15.4464H4.49479C4.32518 15.4464 4.1875 15.3373 4.1875 15.2023V14.9036C4.1875 14.7689 4.32493 14.6595 4.49479 14.6595H4.87093C5.04055 14.6595 5.17822 14.7687 5.17822 14.9036V15.2023C5.17822 15.3373 5.04079 15.4464 4.87093 15.4464Z"
            fill="#1F78B4"
          />
          <path
            d="M8.98812 15.4464H8.61198C8.44236 15.4464 8.30469 15.3373 8.30469 15.2023V14.9036C8.30469 14.7689 8.44212 14.6595 8.61198 14.6595H8.98812C9.15773 14.6595 9.29541 14.7687 9.29541 14.9036V15.2023C9.29541 15.3373 9.15798 15.4464 8.98812 15.4464Z"
            fill="#1F78B4"
          />
          <path
            d="M16.2512 18.3475C13.4633 18.3475 11.1953 16.5463 11.1953 14.3323C11.1953 12.1181 13.4633 10.3169 16.2512 10.3169C19.039 10.3169 21.307 12.1181 21.307 14.3323C21.307 16.5463 19.0387 18.3475 16.2512 18.3475ZM16.2512 11.1035C14.0094 11.1035 12.1858 12.5519 12.1858 14.3323C12.1858 16.1126 14.0094 17.5608 16.2512 17.5608C18.4929 17.5608 20.3165 16.1126 20.3165 14.3323C20.3165 12.5519 18.4927 11.1035 16.2512 11.1035Z"
            fill="#1F78B4"
          />
          <path
            d="M15.7338 15.7985C15.7298 15.7985 15.7258 15.7985 15.7219 15.7983C15.5825 15.7957 15.4507 15.7463 15.3594 15.6628L14.3084 14.6999C14.1294 14.5359 14.1517 14.2874 14.3585 14.1452C14.565 14.0032 14.8782 14.0203 15.057 14.1849L15.7514 14.8211L17.72 13.1837C17.9087 13.0262 18.2224 13.0209 18.42 13.1709C18.6179 13.321 18.6251 13.5698 18.4361 13.7269L16.0916 15.6769C15.9982 15.7546 15.869 15.7985 15.7338 15.7985Z"
            fill="#1F78B4"
          />
        </svg>
      </div>{" "}
      2-Oct-2021 - 21-Oct-2021{" "}
    </div>
  );
}

function Table({ columns, data, text }) {
  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useFilters, // useFilters!
    useGlobalFilter,
    useSortBy,
    usePagination // new
  );

  // Render the UI for your table
  return (
    <>
      {/* table */}
      <div className="mt-4 flex flex-col font-Roboto">
        <div className="-my-2 overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-4">
            <div className=" flex flex-wrap overflow-hidden shadow-lg mt-4 bg-white sm:rounded-sm" style={{borderRadius:"12px"}}>
              {text === "Suppliers" && (
                <div className="flex flex-col m-6 absolute font-Montserrat text-base font-semibold text-gray-100">
                  Suppliers
                </div>
              )}
              {text === "Transactions" && (
                <div className="flex flex-col m-6 absolute font-Montserrat text-base font-semibold text-gray-100">
                  Transactions
                </div>
              )}
              {text === "BOM" && (
                <div className="flex flex-col m-6 absolute font-Montserrat text-base font-semibold text-gray-100">
                  Build Of Material
                </div>
              )}
              {/*Filter*/}
              <h3 className="text-lg font-bold mx-3 mt-5">
                {" "}
                Lead Time of Items{" "}
              </h3>
              <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={state.globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
              {/* {headerGroups.map((headerGroup) =>
                    headerGroup.headers.map((column) =>
                      column.Filter ? (
                        <div className="mt-2 sm:mt-0" key={column.id}>
                          {column.render("Filter")}
                        </div>
                      ) : null
                    )
                  )} */}
              <table
                {...getTableProps()}
                className="min-w-full bg-white divide-y divide-gray-200"
              >
                <thead className="bg-blue-200 border-b-8 border-white">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        // Add the sorting props to control sorting. For this example
                        // we can add them into the header props
                        <th
                          scope="col"
                          className="group px-6 py-3 text-center text-sm font-medium text-white font-Roboto tracking-wider"
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          <div className="flex items-center justify-between">
                            {column.render("Header")}
                            {/* Add a sort direction indicator */}
                            <span>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <SortDownIcon className="w-4 h-4 text-gray-100" />
                                ) : (
                                  <SortUpIcon className="w-4 h-4 text-gray-100" />
                                )
                              ) : (
                                <SortIcon className="w-4 h-4 text-gray-100 opacity-0 group-hover:opacity-100" />
                              )}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="bg-white">
                  {page.map((row, index) => {
                    // new
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className={
                          index % 2 === 0
                            ? "bg-cyan-50 border-b-8 border-white "
                            : "bg-white border-b-8 border-white"
                        }
                      >
                        {row.cells.map((cell) => {
                          return (
                            <td
                              {...cell.getCellProps()}
                              className="px-6 py-0.5 whitespace-nowrap"
                              role="cell"
                            >
                              {cell.column.Cell.name === "defaultRenderer" ? (
                                <div className="text-sm text-black">
                                  {cell.render("Cell")}
                                </div>
                              ) : (
                                cell.render("Cell")
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                {/* <tfoot>
                  <div className="flex flex-row py-4 px-3">
                    <div>
                      <select
                        className="mt-1 form-select appearance-none
                            block
                            px-4
                            py-1
                            text-sm
                            font-Montserrat
                            bg-white bg-clip-padding bg-no-repeat
                            border border-solid border-blue-100
                            rounded-lg
                            transition
                            ease-in-out
                            m-0
                            focus:text-blue-200 focus:border-blue-100 focus:outline-none"
                        value={state.pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                        }}
                      >
                        {[5, 10, 20].map((pageSize) => (
                          <option
                            className="text-blue-200"
                            key={pageSize}
                            value={pageSize}
                          >
                            Show {pageSize}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Filter rows */}
                {/* <div className="pt-2 px-3">
                      {/* Pagination */}
                {/* hidden sm:flex-1 sm:flex sm:items-center sm:justify-between */}
                {/*  <div
                        className="relative z-0 inline-flex -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          className="rounded-l-md"
                          onClick={() => gotoPage(0)}
                          disabled={!canPreviousPage}
                        >
                          <span className="sr-only">First</span>
                          <ChevronDoubleLeftIcon
                            className="h-5 w-5 text-blue-200"
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          onClick={() => previousPage()}
                          disabled={!canPreviousPage}
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeftIcon
                            className="h-5 w-5 text-blue-200"
                            aria-hidden="true"
                          />
                        </button>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-200 font-medium font-Roboto">
                            Page{" "}
                            <span className="font-medium">
                              {state.pageIndex + 1}
                            </span>{" "}
                            of{" "}
                            <span className="font-medium">
                              {pageOptions.length}
                            </span>
                          </span>
                        </div>
                        <button
                          onClick={() => nextPage()}
                          disabled={!canNextPage}
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRightIcon
                            className="h-5 w-5 text-blue-200"
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          className="rounded-r-md"
                          onClick={() => gotoPage(pageCount - 1)}
                          disabled={!canNextPage}
                        >
                          <span className="sr-only">Last</span>
                          <ChevronDoubleRightIcon
                            className="h-5 w-5 text-blue-200"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </tfoot>*/}
              </table>
              <div className="text-center  justify-center m-auto   py-4 px-3">
              {/* Filter rows */}
              <div className="pt-2 px-3 text-center justify-center  m-auto">
                {/* Pagination */}
                {/* hidden sm:flex-1 sm:flex sm:items-center sm:justify-between */}
                <div
                  className="relative z-0 inline-flex -space-x-px "
                  aria-label="Pagination"
                >
                  <div>
                    <select
                      className="mt-1 form-select appearance-none
                          block
                          px-4
                          py-1
                          text-sm
                          font-Montserrat text-blue-900
                          bg-white bg-clip-padding bg-no-repeat
                          font-bold
                          rounded-lg
                          transition
                          ease-in-out
                          m-0
                          focus:text-blue-900 focus:border-blue-100 focus:outline-none"
                      value={state.pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                      }}
                    >
                      {[20].map((pageSize) => (
                        <option
                          className="text-blue-900"
                          key={pageSize}
                          value={pageSize}
                        >
                          See More
                        </option>
                      ))}
                      {[5].map((pageSize) => (
                        <option
                          className="text-blue-900"
                          key={pageSize}
                          value={pageSize}
                        >
                          See Less
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Table;
